import { useMemo, useState } from "react";

import { HStack } from "@chakra-ui/react";

import { Select } from "@tether-web-portal/components/common";
import { IDateRange, IOptionItem } from "@tether-web-portal/types/common";
import { MetricDataInterval, MetricDataIntervalTitleMap } from "@tether-web-portal/types/metricsTypes";
import { useMetricsInterval, useTimezone } from "@tether-web-portal/hooks";
import { DateRangePicker } from "@tether-web-portal/components/datepicker/DateRangePicker";
import { DateTime } from "luxon";
import dayjs from "@tether-web-portal/dayjs-setup";
import {
  getMetricsIntervalButtonDates,
  getMetricsIntervalPickerDates,
  normalizeMetricsIntervalDateRange,
} from "./MetricsIntervalSelector.utils";

interface MetricsIntervalSelectorProps {
  options: MetricDataInterval[];
}

export const MetricsIntervalSelector = ({ options }: MetricsIntervalSelectorProps) => {
  const { metricsCurrentDataInterval, setMetricsInterval, setCustomMetricsInterval, metricsTimeStamps } =
    useMetricsInterval();

  const timezone = useTimezone();

  const [pendingDateRange, setPendingDateRange] = useState<IDateRange>({
    startDate: null,
    endDate: null,
  });

  const { maxDate, minDate } = useMemo(
    () => ({
      maxDate: dayjs().tz(timezone).endOf("day"),
      minDate: dayjs().tz(timezone).subtract(10, "year").startOf("day"),
    }),
    [timezone],
  );
  const timeFrameOptions = useMemo<IOptionItem[]>(
    () =>
      options.map((value) => ({
        value,
        label: MetricDataIntervalTitleMap[value],
      })),
    [options],
  );

  const handleTimeFrameChanged = (option: IOptionItem | null) => {
    if (!option) {
      return;
    }

    setPendingDateRange({ startDate: null, endDate: null });
    if (option.value === MetricDataInterval.CUSTOM) {
      setCustomMetricsInterval(metricsTimeStamps.fromTimestamp, metricsTimeStamps.toTimestamp);
    } else {
      setMetricsInterval(option.value as MetricDataInterval);
    }
  };

  const handleCustomDateRangeChange = (dateRange: IDateRange) => {
    const normalizedDateRange = normalizeMetricsIntervalDateRange(dateRange);
    setPendingDateRange(normalizedDateRange);

    const { startDate, endDate } = normalizedDateRange;
    if (!startDate || !endDate) return;
    const startUtcIso = DateTime.fromISO(startDate.toISOString(), { zone: timezone })
      .startOf("day")
      .toUTC()
      .toISO();
    const endUtcIso = DateTime.fromISO(endDate.toISOString(), { zone: timezone })
      .endOf("day")
      .toUTC()
      .toISO();
    if (!startUtcIso || !endUtcIso) return;
    setCustomMetricsInterval(startUtcIso, endUtcIso);
  };

  const pickerDates = useMemo(
    () => getMetricsIntervalPickerDates(pendingDateRange, metricsTimeStamps),
    [pendingDateRange, metricsTimeStamps],
  );
  const buttonDates = useMemo(
    () => getMetricsIntervalButtonDates(pendingDateRange, metricsTimeStamps),
    [pendingDateRange, metricsTimeStamps],
  );

  return (
    <HStack alignItems="center" columnGap={2}>
      <Select
        options={timeFrameOptions}
        onChange={handleTimeFrameChanged}
        value={metricsCurrentDataInterval}
      />

      <DateRangePicker
        startDate={pickerDates.startDate}
        endDate={pickerDates.endDate}
        buttonStartDate={buttonDates.startDate}
        buttonEndDate={buttonDates.endDate}
        showDateRangeOnButton
        onDatesChange={handleCustomDateRangeChange}
        maxSelectedDays={99}
        maxBookingDate={maxDate}
        minBookingDate={minDate}
        horizontalOffset={-390} // This is terrible, but entire daterange picker needs a re-write...
      />
    </HStack>
  );
};
